import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs';
import { Specialities } from 'server/shared/collections/specialities';
import { Users } from 'server/shared/collections/users';
import { RoleEnum } from 'server/shared/models/role.enum';
import { Speciality } from 'server/shared/models/speciality';
import { DispoUser, MappedSpeciality } from 'server/shared/models/user';
import { rxNgZoneRun } from '../../../helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';

@Component({
  selector: 'app-speciality-select',
  templateUrl: './speciality-select.component.html',
  styleUrls: ['./speciality-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecialitySelectComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  specialityInputSubscription?: Subscription;
  specialitiesSubscription?: Subscription;
  destroyed$ = new Subject<void>();
  allSpecialities: Speciality[] = [];
  customerSpecialityIds?: string[];
  searchTerm?: string;
  filteredSpecialities$ = new BehaviorSubject<Speciality[]>([]);
  recentlyUsedSpecialityIds: string[] = [];

  @Output() selectionChanged = new EventEmitter<Speciality[]>();

  disabled$ = new BehaviorSubject<boolean>(false);
  @Input() set disabled(value: boolean) {
    this.disabled$.next(value);
  }
  get disabled() {
    return this.disabled$.value;
  }

  showHint$ = new BehaviorSubject<boolean>(false);
  @Input() set showHint(value: boolean) {
    this.showHint$.next(value);
  }
  get showHint() {
    return this.showHint$.value;
  }

  _selectedSpecialities$ = new BehaviorSubject<Speciality[]>([]);
  _selectedSpecialityIds: string[] = []; // used as  input to allow input as just ids
  @ViewChild('specialityInput', { static: false }) specialityInput?: NgControl;

  @Input() set selectedSpecialities(specialities: Speciality[]) {
    this.setSelectedSpecialities(specialities);
  }
  get selectedSpecialities() {
    return this._selectedSpecialities$.value;
  }

  @Input() set selectedMappedSpecialities(
    mappedSpecialities: MappedSpeciality[],
  ) {
    if (mappedSpecialities?.length) {
      this._selectedSpecialityIds = mappedSpecialities.map(
        (jobSpeciality) => jobSpeciality.id,
      );
      this.setSelectedSpecialityIds(this._selectedSpecialityIds);
    } else {
      this.setSelectedSpecialities([]);
    }
  }

  currentUserTracker?: Tracker.Computation;
  specialitiesTracker?: Tracker.Computation;

  getParameterSeparator = ',';
  @Input() useQueryParam: string | false = false;

  @Input() doNotUpdateRecentlyUsed: boolean = false;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private customerService: CustomerService,
  ) {
    this.subscribeCustomers();
  }

  ngOnInit(): void {
    this.specialitiesTracker = Tracker.autorun(() => {
      const allSpecialities = Specialities.find().fetch();
      if (allSpecialities.length) {
        this.ngZone.run(() => {
          this.allSpecialities = allSpecialities.sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          this.filterSpecialities();
          if (this._selectedSpecialityIds) {
            this.setSelectedSpecialityIds(this._selectedSpecialityIds);
          }
          this.getInitialQueryParams();
        });
      }
    });

    this.currentUserTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        // Get the currently logged in user to access recently used qualifications
        const currentUser = Users.findOne(Meteor.userId()) as DispoUser;

        if (
          currentUser &&
          currentUser.role === RoleEnum.DISPO &&
          currentUser.recentlyUsed?.specialities
        ) {
          this.recentlyUsedSpecialityIds =
            currentUser.recentlyUsed.specialities;
          this.filterSpecialities();
        }
      });
    });

    if (this.useQueryParam) {
      this.route.queryParams
        .pipe(
          map((params) => params[this.useQueryParam || ''] ?? undefined),
          distinctUntilChanged(),
          rxNgZoneRun(this.ngZone),
          takeUntil(this.destroyed$),
        )
        .subscribe((specialityIdString) => {
          if (specialityIdString) {
            const specialityIds = specialityIdString.split(
              this.getParameterSeparator,
            );
            this._selectedSpecialities$.next(
              this.allSpecialities.filter((speciality) =>
                specialityIds.includes(speciality._id),
              ),
            );
            this.emitValue(false);
          }
        });
    }
  }

  ngAfterViewInit(): void {
    this.specialityInputSubscription =
      this.specialityInput?.valueChanges?.subscribe((value) => {
        if (typeof value == 'string') {
          this.searchTerm = value;
        } else {
          delete this.searchTerm;
        }
        this.filterSpecialities();
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.specialitiesSubscription) {
      this.specialitiesSubscription.unsubscribe();
    }
    if (this.specialityInputSubscription) {
      this.specialityInputSubscription.unsubscribe();
    }
    if (this.currentUserTracker) {
      this.currentUserTracker.stop();
    }
    if (this.specialitiesTracker) {
      this.specialitiesTracker.stop();
    }
  }

  subscribeCustomers() {
    this.customerService.customersOfUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customers) => {
        const displayedSpecialities = customers
          .filter((customer) => customer.visibleSpecialities?.length)
          .reduce(
            (accumulator: string[], customer) =>
              accumulator.concat(customer.visibleSpecialities || []),
            [],
          );
        if (displayedSpecialities.length) {
          this.customerSpecialityIds = displayedSpecialities;
        } else {
          delete this.customerSpecialityIds;
        }
        this.filterSpecialities();
      });
  }

  emitValue(updateQueryParams = true) {
    if (this.useQueryParam && updateQueryParams) {
      this.updateQueryParams({
        [this.useQueryParam]:
          this._selectedSpecialities$.value
            .map((speciality) => speciality._id)
            .join(this.getParameterSeparator) || '',
      });
    }
    this.selectionChanged.emit(this._selectedSpecialities$.value);
  }

  getInitialQueryParams(): void {
    let queryParams = this.route.snapshot.queryParams;
    if (this.useQueryParam && queryParams[this.useQueryParam] !== undefined) {
      const specialityIds = queryParams[this.useQueryParam].split(
        this.getParameterSeparator,
      );
      this._selectedSpecialities$.next(
        this.allSpecialities.filter((speciality) =>
          specialityIds.includes(speciality._id),
        ),
      );
      this.emitValue(false);
    }
  }

  selectedSpeciality(matSelectedEvent: MatAutocompleteSelectedEvent) {
    const speciality = matSelectedEvent.option.value;
    const currentSpecialities = this._selectedSpecialities$.value;
    this._selectedSpecialities$.next([...currentSpecialities, speciality]);
    this.emitValue();

    if (!this.doNotUpdateRecentlyUsed) {
      Meteor.call('addRecentlyUsedSpeciality', speciality._id);
    }

    // @ts-ignore
    this.specialityInput.control.nativeElement.blur();
    // @ts-ignore
    this.specialityInput.control.nativeElement.value = '';
    this.specialityInput?.reset('');
    this.specialityInput?.control?.markAsDirty();
  }

  filterSpecialities() {
    let filteredSpecialities = this.allSpecialities;
    if (this.customerSpecialityIds) {
      filteredSpecialities = filteredSpecialities.filter((speciality) =>
        this.customerSpecialityIds?.includes(speciality._id),
      );
    }
    if (this.searchTerm) {
      filteredSpecialities = filteredSpecialities.filter((speciality) =>
        speciality.name.toLowerCase().includes(this.searchTerm!.toLowerCase()),
      );
    }

    // Sort by recently used first, then alphabetically
    filteredSpecialities = filteredSpecialities.sort((a, b) => {
      const aIndex = this.recentlyUsedSpecialityIds.indexOf(a._id);
      const bIndex = this.recentlyUsedSpecialityIds.indexOf(b._id);

      // If both are in recently used list
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex; // Sort by position in recently used
      }
      // If only a is in recently used
      else if (aIndex !== -1) {
        return -1; // a comes first
      }
      // If only b is in recently used
      else if (bIndex !== -1) {
        return 1; // b comes first
      }
      // Neither are recently used, sort alphabetically
      else {
        return a.name.localeCompare(b.name);
      }
    });

    this.ngZone.run(() => {
      this.filteredSpecialities$.next(filteredSpecialities);
    });
  }

  removeSpeciality(specialityId: string) {
    this._selectedSpecialities$.next(
      this._selectedSpecialities$.value.filter(
        (speciality) => speciality._id !== specialityId,
      ),
    );
    this.emitValue();
  }

  setSelectedSpecialities(specialities: Speciality[]) {
    this._selectedSpecialities$.next(specialities);
  }

  setSelectedSpecialityIds(specialityIds: string[]) {
    this._selectedSpecialities$.next(
      this.allSpecialities.filter((speciality) =>
        specialityIds.includes(speciality._id),
      ),
    );
  }

  private updateQueryParams(queryParams: { [key: string]: string }) {
    this.ngZone.run(() => {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        })
        .then();
    });
  }

  trackBySpecialityId(index: number, speciality: Speciality): string {
    return speciality._id;
  }
}
